import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';

const defaultVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

export default function ScrollReveal({ 
  children, 
  className = '', 
  delay = 0, 
  duration = 0.5,
  direction = 'up',
  distance = 20,
  hero = false,
  ...props 
}) {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(hero);

  const variants = {
    hidden: { 
      opacity: 0, 
      y: direction === 'up' ? distance : direction === 'down' ? -distance : 0,
      x: direction === 'left' ? distance : direction === 'right' ? -distance : 0,
    },
    visible: { opacity: 1, y: 0, x: 0 },
  };

  // For non-hero elements, use a real IntersectionObserver that fires on mount
  useEffect(() => {
    if (hero || isVisible) return;
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0, rootMargin: '50px 0px 50px 0px' }
    );
    observer.observe(el);

    // Fallback: if already in viewport on mount, trigger immediately
    const rect = el.getBoundingClientRect();
    if (rect.top < window.innerHeight + 50 && rect.bottom > -50) {
      setIsVisible(true);
      observer.disconnect();
    }

    return () => observer.disconnect();
  }, [hero, isVisible]);

  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={isVisible ? "visible" : "hidden"}
      transition={{ duration, delay, ease: [0.22, 1, 0.36, 1] }}
      variants={variants}
      className={className}
      {...props}
    >
      {children}
    </motion.div>
  );
}

export function StaggerContainer({ children, className = '', staggerDelay = 0.08, hero = false, ...props }) {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(hero);

  useEffect(() => {
    if (hero || isVisible) return;
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0, rootMargin: '50px 0px 50px 0px' }
    );
    observer.observe(el);

    const rect = el.getBoundingClientRect();
    if (rect.top < window.innerHeight + 50 && rect.bottom > -50) {
      setIsVisible(true);
      observer.disconnect();
    }

    return () => observer.disconnect();
  }, [hero, isVisible]);

  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={isVisible ? "visible" : "hidden"}
      transition={{ staggerChildren: staggerDelay }}
      className={className}
      {...props}
    >
      {children}
    </motion.div>
  );
}

export function StaggerItem({ children, className = '', ...props }) {
  return (
    <motion.div
      variants={defaultVariants}
      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      className={className}
      {...props}
    >
      {children}
    </motion.div>
  );
}
